import React from 'react';
import { Carousel } from '@mantine/carousel';
import { createStyles } from '@mantine/core';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { IconChevronRight } from '~/domains/design-system/icons/IconChevronRight';
import { IconChevronLeft } from '~/domains/design-system/icons/IconChevronLeft';

import { useIsTablet } from '~/domains/common/hooks/mediaQueries';

type SlideshowProps = {
  children: ReactNode;
  /**
   * Width of the slide, can be any css size value.
   * Note: currently it overrides any value in breakpoints, which is not exposed via props */
  slideSize?: ComponentPropsWithoutRef<typeof Carousel>['slideSize'];
};

const useStyles = createStyles((theme) => ({
  container: {
    width: '100%',
  },
  indicator: {
    width: 6,
    height: 6,
    background: theme.colors.gray[3],
    '&[data-active]': {
      background: theme.colors.gray2[6],
    },
    transition: 'width 250ms ease',
  },
  indicators: {
    bottom: 750,
    [`@media(max-width: ${theme.breakpoints.md}px)`]: {
      bottom: 695,
    },
  },
  slide: {
    height: '100%',
    marginTop: /* eslint-disable-line local-rules/use-theme-tokens */ 50,
    marginBottom: /* eslint-disable-line local-rules/use-theme-tokens */ 25,
    width: '100%',
    [`@media(max-width: ${theme.breakpoints.md}px)`]: {
      marginTop: /* eslint-disable-line local-rules/use-theme-tokens */ 100,
    },
  },
  controls: {},
  control: {
    '&[data-inactive]': {
      opacity: 0,
      cursor: 'default',
    },
    background: 'rgba(26, 28, 30, 0.05)',
    border: 0,
    '&:first-of-type': {
      paddingRight: theme.other.spacing._4,
    },
    '&:last-child': {
      paddingLeft: theme.other.spacing._4,
    },
  },
}));

function Slideshow({ children, slideSize }: SlideshowProps) {
  const { classes } = useStyles();
  const isTablet = useIsTablet();

  const numberOfSlidesInSlideshow = React.Children.toArray(children).length;

  return (
    <Carousel
      slideSize={slideSize ?? `calc(100%)`}
      mx="auto"
      draggable
      withControls={!isTablet && numberOfSlidesInSlideshow > 1}
      withIndicators={numberOfSlidesInSlideshow > 1}
      nextControlIcon={<IconChevronRight size={32} strokeWidth={1} />}
      previousControlIcon={<IconChevronLeft size={32} strokeWidth={1.4} />}
      controlSize={46}
      /* eslint-disable-line local-rules/use-theme-tokens */ controlsOffset={60}
      align="start"
      classNames={{
        indicator: classes.indicator,
        indicators: classes.indicators,
        slide: classes.slide,
        control: classes.control,
        controls: classes.controls,
      }}
      containScroll="trimSnaps"
    >
      {children}
    </Carousel>
  );
}

Slideshow.Slide = Carousel.Slide;

export default Slideshow;
